import axios from "./_axios";
import { API } from "./apiEndPoints";

export const getChatterList = (account_id, record_id, type, page = 1) => {
  let result = axios()({
    method: "get",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/chatter/?table=${type}&record_id=${record_id}`,
    params: { page: page },
  });
  return result;
};

/**
 * Fetches the unified chatter feed for a single object (ER-12314).
 * Replaces getChatterList for the combined Chatter drawer: events and comments
 * are interleaved newest-first and paginated by cursor.
 * @param {number|string} account_id
 * @param {string} object_type - lowercase object type (e.g. "employee", "badge")
 * @param {string|number} object_id
 * @param {string} filter - "all" | "comments" | "history"
 * @param {string|null} cursor - pagination cursor from the previous page
 */
export const getChatterFeed = (
  account_id,
  object_type,
  object_id,
  filter = "all",
  cursor = null
) => {
  const params = { object_type, object_id, filter };
  if (cursor) params.cursor = cursor;
  return axios()({
    method: "get",
    url: `${API.ACCOUNT_CHATTER_FEED}${account_id}/chatter-feed/`,
    params,
  });
};

/**
 * Fetches the account-wide activity feed (T3 / Rule 3 card contract).
 * Cursor load-more, object_types filter (OR), search over descriptor + actor.
 * @param {number} account_id
 * @param {object} options
 * @param {string} options.cursor - pagination cursor from the previous page
 * @param {string} options.object_types - comma-separated object types
 * @param {string} options.search - search term (actor name or object descriptor)
 */
export const getAccountActivityStream = (
  account_id,
  { cursor = "", object_types = "", search = "" } = {}
) => {
  const params = {};
  if (cursor) params.cursor = cursor;
  if (object_types) params.object_types = object_types;
  if (search) params.search = search;
  return axios()({
    method: "get",
    url: `${API.ACCOUNT_ACTIVITY_FEED}${account_id}/activity-feed/`,
    params,
  });
};

const commentsUrl = (account_id, suffix = "") =>
  `${API.ACCOUNT_CHATTER_FEED}${account_id}/comments/${suffix}`;

/**
 * Creates a comment or reply (ER-12315). `id` is a client-generated UUID so a
 * retried request is idempotent: the backend returns 200 with the same comment.
 * @param {number|string} account_id
 * @param {{id: string, object_type: string, object_id: string|number, body: string, parent_id?: string|null}} payload
 */
export const createComment = (account_id, payload) =>
  axios()({ method: "post", url: commentsUrl(account_id), data: payload });

/** Re-reads one comment (used by the edit-conflict "Reload"). */
export const getComment = (account_id, comment_id) =>
  axios()({ method: "get", url: commentsUrl(account_id, `${comment_id}/`) });

/**
 * Edits a comment in place. `version` is the version the client read; a stale
 * version is rejected with 409.
 */
export const updateComment = (account_id, comment_id, { body, version }) =>
  axios()({
    method: "patch",
    url: commentsUrl(account_id, `${comment_id}/`),
    data: { body, version },
  });

/** Soft-deletes a comment. */
export const deleteComment = (account_id, comment_id) =>
  axios()({ method: "delete", url: commentsUrl(account_id, `${comment_id}/`) });

/** Resolves a thread (any viewer). */
export const resolveComment = (account_id, comment_id) =>
  axios()({ method: "post", url: commentsUrl(account_id, `${comment_id}/resolve/`) });

/** Reopens a resolved thread (any viewer). */
export const reopenComment = (account_id, comment_id) =>
  axios()({ method: "post", url: commentsUrl(account_id, `${comment_id}/reopen/`) });

/** Batched "seen" receipt for every comment id visible in one render. */
export const markCommentsSeen = (account_id, comment_ids) =>
  axios()({
    method: "post",
    url: commentsUrl(account_id, "seen/"),
    data: { comment_ids },
  });
